import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, ChefHat, PackageCheck, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { money } from "../lib/menu";
import { LINKS } from "../data";

const STEPS = [
  { key: "NEW", label: "Order Received", icon: CheckCircle2 },
  { key: "PREPARING", label: "In the Kitchen", icon: ChefHat },
  { key: "READY", label: "Ready for Pickup", icon: PackageCheck },
];

export default function Confirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const kiosk = typeof window !== "undefined" && localStorage.getItem("kiosk_mode") === "1";

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch (_) { /* keep last known */ }
    };
    load();
    timer = setInterval(load, 6000);
    return () => clearInterval(timer);
  }, [id]);

  // Kiosk: auto-return to the attract screen for the next customer
  useEffect(() => {
    if (!kiosk) return;
    const t = setTimeout(() => navigate("/kiosk"), 25000);
    return () => clearTimeout(t);
  }, [kiosk, navigate]);

  const statusIdx = order ? STEPS.findIndex((s) => s.key === order.status) : 0;
  const currentIdx = statusIdx < 0 ? (order?.status === "COMPLETED" ? 2 : 0) : statusIdx;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center px-5 py-16">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", duration: 0.7 }} className="text-neon-green mb-6">
        <CheckCircle2 size={72} className="neon-glow-green" />
      </motion.div>

      <span className="font-graffiti text-neon-yellow text-3xl -rotate-2">gracias!</span>
      <h1 className="font-heading text-5xl sm:text-6xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green text-center" data-testid="thank-you-heading">Thank You For Your Purchase!</h1>
      <p className="font-body text-neutral-300 text-center mt-3 max-w-md" data-testid="order-received-msg">
        Your order has been <span className="text-neon-green font-semibold">received</span> and sent straight to our kitchen. We&apos;re firing it up now!
      </p>

      {order && (
        <div className="mt-8 w-full max-w-lg" data-testid="confirmation-card">
          <div className="border border-neon-green/25 bg-elevated p-6 text-center">
            <p className="font-body text-sm uppercase tracking-[0.25em] text-neutral-400">Pickup Order Number</p>
            <p className="font-heading text-7xl text-neon-yellow neon-glow-yellow" data-testid="order-number">#{order.order_number}</p>
            <p className="font-body text-neutral-300 mt-1">{order.customer_name} · {order.phone}</p>
            <p className="font-body text-sm text-neutral-500 mt-1 flex items-center justify-center gap-1"><Clock size={14} /> Pickup: {order.pickup_time || "ASAP"}</p>
          </div>

          {/* Status tracker */}
          <div className="mt-6 border border-neon-green/25 bg-elevated p-6">
            <div className="flex justify-between">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = i <= currentIdx;
                return (
                  <div key={s.key} className="flex-1 flex flex-col items-center text-center">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center border-2 transition-colors ${done ? "border-neon-green text-neon-green" : "border-neutral-700 text-neutral-700"}`}>
                      <Icon size={22} />
                    </div>
                    <span className={`font-body text-xs mt-2 ${done ? "text-neon-green" : "text-neutral-600"}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="font-body text-center text-sm text-neutral-400 mt-4" data-testid="live-status">
              {order.status === "READY" ? "Your order is ready! Come grab it." : "We'll update this live as your order cooks."}
            </p>
          </div>

          {/* Items */}
          <div className="mt-6 border border-neon-green/25 bg-elevated p-6">
            <h2 className="font-heading text-2xl uppercase text-white mb-3">Your Items</h2>
            <div className="space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="font-body text-neutral-300">{it.quantity}× {it.name}</span>
                  <span className="font-heading text-neon-yellow">{money(it.unit_price_cents * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between border-t border-neon-green/20 mt-3 pt-3">
              <span className="font-heading text-xl uppercase">Total Paid</span>
              <span className="font-heading text-2xl text-neon-green">{money(order.total_cents)}</span>
            </div>
          </div>

          <a href={LINKS.maps} target="_blank" rel="noopener noreferrer" className="mt-6 flex items-center justify-center gap-2 font-heading text-lg text-neon-green hover:text-neon-yellow transition-colors">
            <MapPin size={18} /> 10 Simas St, Salinas, CA 93901
          </a>
          <Link
            to={kiosk ? "/kiosk" : "/order"}
            data-testid="order-again-btn"
            className="mt-5 block text-center font-heading text-2xl tracking-wide bg-neon-green text-black py-4 hover:bg-neon-yellow transition-colors"
          >
            {kiosk ? "Start New Order" : "Order Again"}
          </Link>
          {kiosk ? (
            <p className="mt-4 text-center font-body text-xs text-neutral-600">Returning to start automatically…</p>
          ) : (
            <Link to="/" data-testid="back-home-link" className="mt-4 block text-center font-body text-sm text-neutral-500 hover:text-neon-green transition-colors">Back to home</Link>
          )}
        </div>
      )}
    </div>
  );
}
